// src/pages/TeamSchedulePage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { departmentIconMap } from "../utils/departmentIcons";

function TeamSchedulePage() {
  const { user, authFetch } = useAuth();

  const days = useMemo(() => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"], []);
  const todayCol = useMemo(() => new Date().getDay(), []);

  const [week, setWeek] = useState(null);
  const [departmentLabel, setDepartmentLabel] = useState("—");
  const [teamRows, setTeamRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [deptThemes, setDeptThemes] = useState({ byKey: {}, list: [] });
  const [notPublished, setNotPublished] = useState(false);

  const [allowedDepartments, setAllowedDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);
  const [deptOpen, setDeptOpen] = useState(false);
  const deptDropdownRef = useRef(null);

  const departmentId = selectedDepartmentId;
  const currentUserId = user?.id ?? user?.user_id ?? null;

  function getDepartmentDisplayName(id) {
    if (id === "ALL") return "All Departments";
    const found = allowedDepartments.find((d) => Number(d.id) === Number(id));
    return found?.name || "Select department";
  }

  async function readJsonSafe(res) {
    if (!res) return null;

    let text = "";
    try {
      text = await res.text();
    } catch {
      return null;
    }

    const t = String(text || "").trim();
    if (!t) return null;

    try {
      return JSON.parse(t);
    } catch {
      return { error: "Non-JSON response", raw: t };
    }
  }

  function attendanceToShortLabel(noteOrStatus) {
    const s = String(noteOrStatus || "").toUpperCase();
    if (!s) return "";

    if (s.includes("NO_CALL_NO_SHOW") || s.includes("NCNS")) return "NCNS";
    if (s.includes("SICK")) return "SICK";
    if (s.includes("LATE")) return "LATE";
    if (s.includes("EMERGENCY")) return "EMERGENCY";
    if (s.includes("OTHER")) return "OTHER";

    return "";
  }

  async function fetchAttendanceWeekMap(weekId, deptId, deptList = []) {
    const map = new Map();

    if (!weekId) return map;

    try {
      let allRows = [];

      if (deptId && deptId !== "ALL") {
        const res = await authFetch(
          `/api/attendance/week?weekId=${weekId}&departmentId=${deptId}`,
          { cache: "no-store" }
        );

        if (!res.ok) return map;

        const data = await readJsonSafe(res);
        allRows = Array.isArray(data?.rows) ? data.rows : [];
      } else {
        const results = await Promise.all(
          (deptList || []).map(async (d) => {
            const res = await authFetch(
              `/api/attendance/week?weekId=${weekId}&departmentId=${d.id}`,
              { cache: "no-store" }
            );

            if (!res.ok) return [];

            const data = await readJsonSafe(res);
            return Array.isArray(data?.rows) ? data.rows : [];
          })
        );

        allRows = results.flat();
      }

      for (const r of allRows) {
        const uid = Number(r.user_id);
        const dow = Number(r.day_of_week);

        if (!uid) continue;
        if (!Number.isFinite(dow) || dow < 0 || dow > 6) continue;

        const label = attendanceToShortLabel(r.status || r.attendance_status || "");
        if (!label) continue;

        if (!map.has(uid)) map.set(uid, {});
        map.get(uid)[dow] = label;
      }
    } catch (e) {
      return map;
    }

    return map;
  }

  useEffect(() => {
    function handleClickOutside(e) {
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(e.target)) {
        setDeptOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setErr("");

        const myDeptRes = await authFetch("/api/my-departments", { cache: "no-store" });
        const myDepts = await readJsonSafe(myDeptRes);

        if (!myDeptRes.ok) throw new Error("Failed to load departments");

        setAllowedDepartments(myDepts || []);

        let activeDept = selectedDepartmentId;

        if (!activeDept) {
          const userDeptId =
            user?.department_id ??
            user?.departmentId ??
            user?.dept_id ??
            user?.deptId ??
            null;

          const role = String(user?.role || "").toLowerCase();

          if (role !== "admin" && role !== "superadmin" && userDeptId != null) {
            const ownDept = (myDepts || []).find(
              (d) => Number(d.id) === Number(userDeptId)
            );

            if (ownDept) {
              activeDept = ownDept.id;
            }
          }

          if (!activeDept && myDepts?.length) {
            activeDept = myDepts[0].id;
          }

          if (activeDept) {
            setSelectedDepartmentId(activeDept);
          }
        }

        if (!activeDept) {
          throw new Error("No department access.");
        }

        const currentWeekRes = await authFetch("/api/weeks/current", { cache: "no-store" });
        const currentWeek = await readJsonSafe(currentWeekRes);

        if (!currentWeekRes.ok) {
          throw new Error(currentWeek?.error || "Failed to load current week");
        }

        if (!currentWeek?.id) {
          throw new Error("No current week found");
        }

        setWeek(currentWeek);
        const chosen = currentWeek;

        const scheduleUrl =
          activeDept === "ALL"
            ? `/api/team-schedule?weekId=${chosen.id}`
            : `/api/team-schedule?weekId=${chosen.id}&departmentId=${activeDept}`;

        const [depRes, schedRes, attendanceMap] = await Promise.all([
          authFetch("/api/departments?themes=1", { cache: "no-store" }),
          authFetch(scheduleUrl, { cache: "no-store" }),
          fetchAttendanceWeekMap(chosen.id, activeDept, myDepts || []),
        ]);

        const deps = await readJsonSafe(depRes);
        if (!depRes.ok) throw new Error(deps?.error || "Failed to load departments");

        const data = await readJsonSafe(schedRes);
        if (!schedRes.ok) throw new Error(data?.error || "Failed to load team schedule");

        if (data?.week?.id) {
          setWeek(data.week);
        }

        if (activeDept === "ALL") {
          setDepartmentLabel("All Departments");
        } else {
          const dep = (deps || []).find((d) => Number(d.id) === Number(activeDept));
          setDepartmentLabel(dep?.name || "—");
        }

        const cleanStr = (v) => {
          const s = String(v ?? "").trim();
          return s ? s : null;
        };

        const depList = Array.isArray(deps) ? deps : [];
        const byKey = {};

        for (const d of depList) {
          const key = normKey(d?.name);
          if (!key) continue;

          const icon =
            cleanStr(d.icon) ||
            cleanStr(d.icon_name) ||
            cleanStr(d.iconName) ||
            null;
          const color =
            cleanStr(d.color) ||
            cleanStr(d.department_color) ||
            cleanStr(d.departmentColor) ||
            null;

          byKey[key] = {
            id: d.id,
            name: d.name,
            color,
            icon,
          };
        }

        setDeptThemes({
          byKey,
          list: depList.map((d) => ({
            id: d.id,
            name: d.name,
            color:
              cleanStr(d.color) ||
              cleanStr(d.department_color) ||
              cleanStr(d.departmentColor) ||
              null,
            icon:
              cleanStr(d.icon) ||
              cleanStr(d.icon_name) ||
              cleanStr(d.iconName) ||
              null,
          })),
        });

        if (data && !Array.isArray(data) && data.notPublished) {
          setNotPublished(true);
          setTeamRows([]);
          return;
        }

        setNotPublished(false);

        const byUser = new Map();
        const rows = Array.isArray(data) ? data : (data?.rows || []);

        for (const r of rows) {
          const key = r.user_id;
          if (!key) continue;

          if (!byUser.has(key)) {
            byUser.set(key, {
              user_id: r.user_id,
              name: r.user_name || "—",
              role: r.user_role || "user",
              schedule: Array(7).fill(null),
            });
          }

          const idx = Number(r.day_of_week);
          if (Number.isFinite(idx) && idx >= 0 && idx <= 6) {
            byUser.get(key).schedule[idx] = r;
          }
        }

        const grid = Array.from(byUser.values()).map((m) => {
          const schedule = (m.schedule || []).map((cell) => (cell ? { ...cell } : null));
          const attForUser = attendanceMap.get(Number(m.user_id)) || {};

          for (let i = 0; i <= 6; i++) {
            const label = attForUser[i];
            const cell = schedule[i];

            if (label && cell && String(cell.duty_type || "").toUpperCase() === "SHIFT") {
              cell.note = `${days[i]} ${label}`;
            }
          }

          return {
            ...m,
            schedule,
            scheduleText: schedule.map((cell) => formatCell(cell)),
          };
        });

        setTeamRows(grid);
      } catch (e) {
        setErr(e.message || "Error");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [authFetch, departmentId, currentUserId, days, todayCol, user]);

  const fmtShort = (iso) => {
    const d = (() => {
      const s = String(iso || "").trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return null;
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    })();
    if (!d) return String(iso || "");
    return d.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  };

  const weekLabel =
    week?.start_date && week?.end_date
      ? `${fmtShort(week.start_date)} – ${fmtShort(week.end_date)}`
      : week?.title
      ? week.title
      : "—";

  function attendanceToLabel(status) {
    const s = String(status || "").toUpperCase();
    if (!s) return "";

    if (s.includes("NO_CALL_NO_SHOW") || s === "NCNS") return "NCNS";
    if (s.includes("SICK")) return "SICK";
    if (s.includes("LATE")) return "LATE";
    if (s.includes("EMERGENCY")) return "EMERGENCY";
    if (s.includes("OTHER")) return "OTHER";
    return s;
  }

  function attendanceIcon(label) {
    const u = String(label || "").toUpperCase();
    if (u === "NCNS") return "🚫";
    if (u === "SICK") return "🤒";
    if (u === "LATE") return "⏰";
    if (u === "EMERGENCY") return "🚨";
    if (u === "OTHER") return "📝";
    return "⚠️";
  }

  function extractAttendanceFromEntry(entry) {
    if (!entry) return "";

    const direct =
      entry.attendance_status ||
      entry.attendanceStatus ||
      entry.attendance ||
      entry.status ||
      "";

    const d = attendanceToLabel(direct);
    if (d) return d;

    const note = String(entry.note || "");
    const m = note.match(/\b(NCNS|SICK|LATE|EMERGENCY|OTHER)\b/i);
    return m ? attendanceToLabel(m[1]) : "";
  }

  function formatCell(entry) {
    if (!entry) return "-";

    const dutyType = String(entry.duty_type || "").toUpperCase();

    if (dutyType === "DAY_OFF") return "DAY_OFF";
    if (dutyType === "VACATION") return "VACATION";
    if (dutyType === "TRAINING") return "TRAINING";
    if (dutyType === "LOA") return "LOA";

    const att = extractAttendanceFromEntry(entry);
    if (att) return att;

    const time =
      entry.start_time && entry.end_time
        ? `${entry.start_time}–${entry.end_time}`
        : "";

    const loc = String(entry.duty_location || "").trim().toUpperCase();
    const sub = String(
      entry.sub_duty || entry.subDuty || entry.sub_duty_name || ""
    ).trim().toUpperCase();

    return {
      label: loc || time || dutyType || "-",
      subLabel: sub || "",
    };
  }

  function calculateStaffing(rows) {
    let scheduled = 0;
    let present = 0;

    for (const r of rows) {
      const duty = String(r.duty_type || "").toUpperCase();

      if (duty === "SHIFT") {
        scheduled++;

        const note = String(r.note || "").toUpperCase();

        if (!note.includes("NCNS") && !note.includes("SICK")) {
          present++;
        }
      }
    }

    return { scheduled, present };
  }

  function normKey(s) {
    return String(s || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, " ")
      .trim();
  }

  function hexToRgba(hex, a = 0.18) {
    const h = String(hex || "").trim().replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  function hexToRgb(hex) {
    if (!hex) return null;
    const h = String(hex || "").trim().replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;

    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return { r, g, b };
  }

  function bestTextColor(bgHex) {
    const rgb = hexToRgb(bgHex);
    if (!rgb) return "#0f172a";

    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness > 160 ? "#0f172a" : "#ffffff";
  }

  function darkenHex(hex, amount = 0.35) {
    const rgb = hexToRgb(hex);
    if (!rgb) return "#0f172a";

    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
    const factor = 1 - amount;

    return `rgb(${clamp(rgb.r * factor)}, ${clamp(rgb.g * factor)}, ${clamp(rgb.b * factor)})`;
  }

  function normalizeIcon(icon) {
    const s = String(icon ?? "").trim();
    return s ? s : "";
  }

  function renderDeptIcon(iconKey, size = 14, color = "currentColor") {
    const key = String(iconKey || "").trim();
    const Comp = departmentIconMap?.[key];

    if (!Comp) return null;

    return React.createElement(Comp, {
      size,
      color,
      style: { color },
    });
  }

  const getThemeForEntry = (entry) => {
    if (!entry) return null;

    const loc = normKey(entry.duty_location || "");
    if (!loc) return null;

    if (deptThemes.byKey[loc]) return deptThemes.byKey[loc];

    for (const d of deptThemes.list) {
      const nameKey = normKey(d.name);
      if (nameKey && loc.includes(nameKey)) return d;
    }

    return null;
  };

  function tooltipFor(entry) {
    if (!entry) return "No duty";

    const duty = entry.duty_type || "-";
    const time = entry.start_time && entry.end_time ? `${entry.start_time} – ${entry.end_time}` : "";
    const loc = entry.duty_location ? String(entry.duty_location) : "";
    const sub = entry.sub_duty || entry.subDuty || entry.sub_duty_name || "";
    const note = entry.note || "";

    const att = extractAttendanceFromEntry(entry);

    const parts = [
      `Duty: ${duty}`,
      time ? `Time: ${time}` : null,
      loc ? `Location: ${loc}` : null,
      sub ? `Sub-duty: ${sub}` : null,
      note ? `Note: ${note}` : null,
      att ? `Marked by Admin: ${att}` : null,
    ].filter(Boolean);

    return parts.join(" • ");
  }

  const getCellStyle = (value, entry) => {
    const base = {
      border: "1px solid rgba(2,6,23,.08)",
      padding: "10px 10px",
      fontSize: "12px",
      textAlign: "center",
      whiteSpace: "normal",
      lineHeight: "1.25",
      borderRadius: "10px",
      fontWeight: 900,
      backgroundSize: "200% 200%",
      backgroundPosition: "0% 50%",
      transition: "transform .14s ease, box-shadow .14s ease, background-position .20s ease",
      color: "#0f172a",
    };

    const upper = String(value || "").toUpperCase();
    const isAttendance = ["NCNS", "SICK", "LATE", "EMERGENCY", "OTHER"].includes(upper);

    if (entry && String(entry.duty_type || "").toUpperCase() === "SHIFT" && !isAttendance) {
      const theme = getThemeForEntry(entry);
      const bg1 = hexToRgba(theme?.color, 0.22);
      const bg2 = hexToRgba(theme?.color, 0.10);
      const textColor = darkenHex(theme?.color, 0.35);

      if (theme?.color && bg1 && bg2) {
        return {
          ...base,
          backgroundImage: `linear-gradient(135deg, ${bg1} 0%, ${bg2} 55%, ${bg1} 100%)`,
          border: `1px solid ${hexToRgba(theme.color, 0.35) || "rgba(2,6,23,.10)"}`,
          color: textColor,
          fontWeight: 900,
        };
      }
    }

    if (!value || value === "-") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        border: "1px dashed rgba(2,6,23,.10)",
        color: "#9ca3af",
        fontWeight: 800,
      };
    }

    if (upper === "SICK") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #ffc6ba 0%, #ffe4de 55%, #ffc6ba 100%)",
        color: "#7c2d12",
        border: "1px solid rgba(124,45,18,.25)",
      };
    }

    if (upper === "NCNS" || upper.includes("NO CALL")) {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #e6ffba 0%, #f0ffd6 55%, #e6ffba 100%)",
        color: "#365314",
        border: "1px solid rgba(54,83,20,.25)",
      };
    }

    if (upper === "LATE") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #fde68a 0%, #fef3c7 55%, #fde68a 100%)",
        color: "#854d0e",
        border: "1px solid rgba(133,77,14,.25)",
      };
    }

    if (upper === "EMERGENCY") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #fecaca 0%, #fee2e2 55%, #fecaca 100%)",
        color: "#7f1d1d",
        border: "1px solid rgba(127,29,29,.25)",
      };
    }

    if (upper === "OTHER") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 55%, #e2e8f0 100%)",
        color: "#0f172a",
        border: "1px solid rgba(2,6,23,.10)",
      };
    }

    if (upper === "FLOAT") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #ffe4e6 0%, #fecdd3 55%, #fda4af 100%)",
        color: "#9f1239",
        border: "1px solid rgba(159,18,57,.25)",
        fontWeight: 950,
      };
    }

    if (upper === "DAY_OFF") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 55%, #a7f3d0 100%)",
        color: "#166534",
        border: "1px solid #86efac",
      };
    }

    if (upper.includes("VAC")) {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #ffedd5 0%, #fed7aa 55%, #fdba74 100%)",
        color: "#9a3412",
        border: "1px solid #fdba74",
      };
    }

    if (upper === "TRAINING") {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #fef9c3 0%, #fde68a 55%, #facc15 100%)",
        color: "#854d0e",
        border: "1px solid #facc15",
      };
    }

    if (upper.includes("GATEKEEPER") || upper.includes("CONTROL ROOM") || upper.includes("THAW")) {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 55%, #7dd3fc 100%)",
        color: "#075985",
        border: "1px solid #7dd3fc",
      };
    }

    if (/\d/.test(upper)) {
      return {
        ...base,
        backgroundImage: "linear-gradient(135deg, #f8fafc 0%, #eef2f7 55%, #e2e8f0 100%)",
        color: "#111827",
        border: "1px solid rgba(2,6,23,.10)",
        fontWeight: 850,
      };
    }

    return {
      ...base,
      backgroundImage: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 55%, #cbd5e1 100%)",
      color: "#0f172a",
      border: "1px solid rgba(2,6,23,.10)",
      fontWeight: 850,
    };
  };

  const getChipStyle = (value, entry) => {
    const s = getCellStyle(value, entry);
    return {
      backgroundImage: s.backgroundImage,
      backgroundSize: s.backgroundSize,
      backgroundPosition: s.backgroundPosition,
      color: s.color,
      border: s.border,
    };
  };

  if (loading)
    return (
      <div className="team-schedule-page">
        <p>Loading team schedule...</p>
      </div>
    );

  if (err)
    return (
      <div className="team-schedule-page">
        <p style={{ color: "red" }}>{err}</p>
      </div>
    );

  return (
    <div className="team-schedule-page">
      <style>{`
        :root{
          --bg:#f3f6fb;
          --card:#ffffff;
          --text:#0f172a;
          --muted:#64748b;
          --line: rgba(2, 6, 23, .08);
          --shadow: 0 14px 26px rgba(2, 6, 23, .08);
          --radius: 18px;
          --sideW: 78px;
        }

        .team-schedule-page{
          padding: 8px 8px 18px;
          color: var(--text);
        }

        .team-schedule-header{
          display:flex;
          align-items:flex-start;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .team-schedule-header h1{
          margin: 0;
          font-size: 34px;
          font-weight: 950;
          letter-spacing: -0.6px;
        }

        .team-schedule-header p{
          margin: 6px 0 0;
          color: var(--muted);
          font-weight: 700;
        }

        .team-schedule-filters{
          display:flex;
          gap: 10px;
          flex-wrap: wrap;
          align-items:center;
          justify-content:flex-end;
        }

        .filter-chip{
          border: 1px solid rgba(2,6,23,.10);
          background: #fff;
          box-shadow: 0 10px 18px rgba(2,6,23,.06);
          padding: 8px 12px;
          border-radius: 999px;
          font-weight: 900;
          color: var(--text);
          white-space: nowrap;
          font-size: 13px;
        }

        .filter-chip > span{
          font-weight: 950;
          color: #2563eb;
          margin-left: 6px;
        }

          .filter-chip--select{
            display: flex;
            align-items: center;
            gap: 10px;
            width: auto;
            min-width: 320px;
            max-width: 420px;
          }

        .filter-chip-label{
          font-weight: 900;
          color: #0f172a;
          flex: 0 0 auto;
        }

        .dept-dropdown{
          position: relative;
          width: 220px;
          min-width: 220px;
          max-width: 220px;
        }

        .dept-dropdown-btn{
          width: 100%;
          min-width: 0;
          border: 1px solid rgba(37,99,235,.14);
          background: linear-gradient(135deg, #f8fbff 0%, #eef4ff 100%);
          color: #2563eb;
          border-radius: 12px;
          padding: 10px 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 900;
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.9),
            0 6px 14px rgba(37,99,235,.08);
          transition: border-color .18s ease, box-shadow .18s ease, transform .12s ease;
        }

        .dept-dropdown-btn:hover{
          border-color: rgba(37,99,235,.28);
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.9),
            0 10px 20px rgba(37,99,235,.10);
        }

        .dept-dropdown-btn.open{
          border-color: #2563eb;
          box-shadow:
            0 0 0 3px rgba(37,99,235,.10),
            0 10px 22px rgba(37,99,235,.12);
        }

        .dept-dropdown-value{
          flex: 1 1 auto;
          min-width: 0;
          text-align: left;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dept-dropdown-arrow{
          font-size: 15px;
          line-height: 1;
          transition: transform .18s ease;
          flex: 0 0 auto;
          color: #2563eb;
          margin-left: 0 !important;
        }

        .dept-dropdown-arrow.open{
          transform: rotate(180deg);
        }

        .dept-dropdown-menu{
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          left: auto;
          width: 220px;
          min-width: 220px;
          max-width: 220px;
          background: #ffffff;
          border: 1px solid rgba(2,6,23,.08);
          border-radius: 14px;
          box-shadow: 0 18px 34px rgba(2,6,23,.14);
          padding: 8px;
          z-index: 50;
          max-height: 260px;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .dept-option{
          display: block;
          width: 100%;
          border: none;
          background: transparent;
          text-align: left;
          padding: 10px 11px;
          white-space: nowrap;
          border-radius: 12px;
          color: #0f172a;
          font-size: 12px;
          font-weight: 850;
          cursor: pointer;
          transition: background .15s ease, color .15s ease;
        }

        .dept-option:hover{
          background: #eff6ff;
          color: #1d4ed8;
        }

        .dept-option.active{
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: #ffffff;
          box-shadow: 0 8px 18px rgba(37,99,235,.18);
        }

        .team-schedule-card{
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          overflow: hidden;
        }

        .team-schedule-card-header{
          padding: 16px 16px 10px;
          border-bottom: 1px solid rgba(2,6,23,.06);
        }

        .team-schedule-card-header h2{
          margin: 0;
          font-size: 20px;
          font-weight: 950;
        }

        .team-schedule-card-header p{
          margin: 6px 0 0;
          color: var(--muted);
          font-weight: 700;
          font-size: 13px;
        }

        .team-table-wrapper{
          overflow-x: auto;
          padding: 10px 12px 12px;
        }

        .team-table-wrapper::-webkit-scrollbar{ height: 10px; }

        .team-table-wrapper::-webkit-scrollbar-thumb{
          background: rgba(2,6,23,.18);
          border-radius: 999px;
        }

        .team-schedule-table{
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          background: #fff;
          border: 1px solid rgba(2,6,23,.06);
          border-radius: 14px;
          overflow: hidden;
          min-width: 960px;
        }

        .team-schedule-table thead th{
          position: sticky;
          top: 0;
          z-index: 2;
          background: #f8fafc;
          color: #0f172a;
          font-weight: 950;
          font-size: 12px;
          letter-spacing: .3px;
          text-transform: uppercase;
          padding: 12px 12px;
          border-bottom: 1px solid rgba(2,6,23,.08);
          white-space: nowrap;
        }

        .ts-today-th{
          position: relative;
        }

        .ts-today-th::after{
          content:"";
          position:absolute;
          inset: -6px -6px;
          border-radius: 12px;
          background: radial-gradient(520px 140px at 50% 50%, rgba(37,99,235,.16), rgba(37,99,235,0) 62%);
          pointer-events:none;
        }

        .team-schedule-table tbody td{
          padding: 10px 12px;
          border-bottom: 1px solid rgba(2,6,23,.06);
          border-right: 1px solid rgba(2,6,23,.06);
          font-size: 13px;
          color: #0f172a;
          white-space: nowrap;
          vertical-align: middle;
          background: #fff;
        }

        .team-schedule-table tbody tr:last-child td{ border-bottom: none; }
        .team-schedule-table tbody td:last-child{ border-right: none; }

        .sticky-col{
          position: sticky;
          left: 0;
          z-index: 3;
          background: #fff;
        }

        .sticky-col-2{
          position: sticky;
          left: 260px;
          z-index: 3;
          background: #fff;
        }

        .col-staff{ width: 260px; min-width: 260px; max-width: 260px; }
        .col-role{ width: 130px; min-width: 130px; max-width: 130px; }

        .team-schedule-table tbody tr:hover td{
          background: #f8fafc;
        }

        .team-row--me td{
          background: rgba(37,99,235,.05);
        }

        .team-row--me:hover td{
          background: rgba(37,99,235,.08);
        }

        .team-row--me .sticky-col,
        .team-row--me .sticky-col-2{
          background: rgba(37,99,235,.05);
        }

        .team-name-pill{
          display:inline-flex;
          align-items:center;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 999px;
          border: 1px solid rgba(37,99,235,.18);
          background: rgba(37,99,235,.08);
          font-weight: 950;
          color: #0f172a;
          max-width: 230px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .team-dot{
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: rgba(37,99,235,.45);
          flex: 0 0 auto;
        }

        .team-me-tag{
          margin-left: 8px;
          padding: 4px 8px;
          border-radius: 999px;
          background: rgba(37,99,235,.12);
          border: 1px solid rgba(37,99,235,.18);
          color: #1d4ed8;
          font-weight: 950;
          font-size: 11px;
        }

        .ts-cell:hover{
          background-position: 100% 50% !important;
          transform: translateY(-1px);
          box-shadow: 0 10px 18px rgba(2,6,23,.10);
        }

        .ts-today-cell{
          box-shadow: inset 0 0 0 9999px rgba(37,99,235,.03);
          border-radius: 12px;
          padding: 2px;
        }

        .team-legend{
          display:flex;
          flex-wrap: wrap;
          gap: 14px;
          padding: 12px 14px 14px;
          border-top: 1px solid rgba(2,6,23,.06);
          align-items:center;
        }

        .legend-item{
          display:flex;
          align-items:center;
          gap: 8px;
          color: #4b5563;
          font-weight: 850;
          font-size: 13px;
        }

        .legend-dot{
          width: 12px;
          height: 12px;
          border-radius: 999px;
          border: 1px solid rgba(2,6,23,.10);
          background-size: 200% 200%;
          background-position: 0% 50%;
        }

        .legend-dot--off{
          background-image: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 55%, #a7f3d0 100%);
          border-color:#86efac;
        }

        .legend-dot--vac{
          background-image: linear-gradient(135deg, #ffedd5 0%, #fed7aa 55%, #fdba74 100%);
          border-color:#fdba74;
        }

        .legend-dot--train{
          background-image: linear-gradient(135deg, #fef9c3 0%, #fde68a 55%, #facc15 100%);
          border-color:#facc15;
        }

        .legend-dot--special{
          background-image: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 55%, #7dd3fc 100%);
          border-color:#7dd3fc;
        }

        .team-cards{
          display: none;
          padding: 12px;
        }

        .member-card{
          border: 1px solid rgba(2,6,23,.08);
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 10px 18px rgba(2,6,23,.06);
          padding: 12px;
          margin-bottom: 12px;
        }

        .member-head{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 10px;
        }

        .member-name{
          font-weight: 950;
          font-size: 15px;
          display:flex;
          align-items:center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 999px;
          background: rgba(37,99,235,0.08);
          border: 1px solid rgba(37,99,235,0.12);
        }

        .member-role{
          font-weight: 900;
          color: #64748b;
          font-size: 13px;
        }

        .member-me{
          padding: 4px 8px;
          border-radius: 999px;
          background: rgba(37,99,235,.10);
          border: 1px solid rgba(37,99,235,.18);
          color: #1d4ed8;
          font-weight: 950;
          font-size: 11px;
          white-space: nowrap;
        }

        .day-row{
          display:flex;
          align-items:center;
          justify-content: space-between;
          gap: 10px;
          padding: 8px 0;
          border-top: 1px solid rgba(2,6,23,.06);
        }

        .day-label{
          font-weight: 950;
          color: #0f172a;
          font-size: 13px;
          width: 46px;
          flex: 0 0 46px;
        }

        .day-chip{
          flex: 1;
          display:flex;
          justify-content:flex-end;
        }

        .chip{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          padding: 8px 10px;
          border-radius: 12px;
          font-weight: 900;
          font-size: 12px;
          white-space: nowrap;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          background-size: 200% 200%;
          backgroundPosition: 0% 50%;
          transition: background-position .20s ease, transform .14s ease, box-shadow .14s ease;
        }

        .chip:hover{
          background-position: 100% 50%;
          transform: translateY(-1px);
          box-shadow: 0 10px 18px rgba(2,6,23,.10);
        }

        @media (max-width: 900px){
          .team-schedule-page{
            padding: 8px 6px 90px;
            overflow-x: hidden;
          }

          .team-schedule-header{
            display: block;
            margin-bottom: 10px;
          }

          .team-schedule-header h1{
            font-size: 26px;
            line-height: 1.15;
          }

          .team-schedule-header p{
            font-size: 12px;
            line-height: 1.35;
            margin-top: 4px;
          }

          .team-schedule-filters{
            display: flex;
            flex-direction: column;
            align-items: stretch;
            justify-content: flex-start;
            gap: 8px;
            width: 100%;
            min-width: 0;
            margin-top: 10px;
          }

          .filter-chip{
            width: 100%;
            min-width: 0;
            box-sizing: border-box;
            white-space: normal;
            word-break: break-word;
            overflow-wrap: anywhere;
            border-radius: 14px;
            font-size: 12px;
            line-height: 1.35;
          }

          .filter-chip > span{
            display: inline;
            margin-left: 4px;
          }

          .filter-chip--select{
            width: 100%;
            min-width: 0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
          }

          .filter-chip-label{
            font-size: 12px;
          }

          .dept-dropdown{
            width: 100%;
            min-width: 0;
          }

          .dept-dropdown-btn{
            width: 100%;
            min-width: 0;
            font-size: 12px;
            padding: 10px 12px;
            border-radius: 12px;
          }

          .dept-dropdown-menu{
            width: 100%;
            min-width: 100%;
            max-width: 100%;
          }

          .dept-option{
            font-size: 12px;
            padding: 10px 11px;
          }

          .team-schedule-card{
            border-radius: 16px;
          }

          .team-schedule-card-header{
            padding: 12px;
          }

          .team-schedule-card-header h2{
            font-size: 18px;
            line-height: 1.2;
          }

          .team-schedule-card-header p{
            font-size: 12px;
            line-height: 1.35;
          }

          .team-table-wrapper{
            display: none;
          }

          .team-cards{
            display: block;
            padding: 10px;
          }

          .member-card{
            padding: 10px;
            margin-bottom: 10px;
          }

          .member-head{
            align-items: flex-start;
            flex-direction: column;
            gap: 8px;
          }

          .member-name{
            max-width: 100%;
            min-width: 0;
            font-size: 14px;
            flex-wrap: wrap;
          }

          .member-role{
            font-size: 12px;
          }

          .day-row{
            gap: 8px;
          }

          .day-label{
            width: 42px;
            flex: 0 0 42px;
            font-size: 12px;
          }

          .day-chip{
            min-width: 0;
          }

          .chip{
            max-width: 100%;
            min-width: 0;
            font-size: 11px;
            padding: 7px 9px;
            white-space: normal;
            text-align: center;
            line-height: 1.2;
          }

          .team-legend{
            gap: 10px;
            padding: 10px 12px 12px;
          }

          .legend-item{
            font-size: 12px;
          }
        }
      `}</style>

      <div className="team-schedule-header">
        <div>
          <h1>Team Schedule</h1>
          <p>
            Showing weekly duties for <strong>{departmentLabel}</strong> team.
          </p>
        </div>

        <div className="team-schedule-filters">
          <div className="filter-chip">
            Week: <span>{weekLabel}</span>
          </div>

          <div className="filter-chip filter-chip--select">
            <div className="filter-chip-label">Department</div>

            <div className="dept-dropdown" ref={deptDropdownRef}>
              <button
                type="button"
                className={`dept-dropdown-btn ${deptOpen ? "open" : ""}`}
                onClick={() => setDeptOpen((v) => !v)}
              >
                <span className="dept-dropdown-value">
                  {getDepartmentDisplayName(selectedDepartmentId ?? "ALL")}
                </span>
                <span className={`dept-dropdown-arrow ${deptOpen ? "open" : ""}`}>⌄</span>
              </button>

              {deptOpen && (
                <div className="dept-dropdown-menu">
                  <button
                    type="button"
                    className={`dept-option ${(selectedDepartmentId ?? "ALL") === "ALL" ? "active" : ""}`}
                    onClick={() => {
                      setSelectedDepartmentId("ALL");
                      setDeptOpen(false);
                    }}
                  >
                    All Departments
                  </button>

                  {allowedDepartments.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className={`dept-option ${Number(selectedDepartmentId) === Number(d.id) ? "active" : ""}`}
                      onClick={() => {
                        setSelectedDepartmentId(d.id);
                        setDeptOpen(false);
                      }}
                    >
                      {d.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-chip filter-chip--user">
            Logged in as <span>{user?.username || "User"}</span>
          </div>
        </div>
      </div>

      <div className="team-schedule-card">
        <div className="team-schedule-card-header">
          {notPublished && (
            <div style={{ padding: "12px 16px", fontWeight: 900, color: "#b45309" }}>
              This schedule is not published yet.
            </div>
          )}

          <div>
            <h2>
              {departmentLabel} – Weekly View
              {teamRows.length > 0 && (
                <span
                  style={{
                    marginLeft: 12,
                    fontSize: "13px",
                    fontWeight: 900,
                    color: "#2563eb"
                  }}
                >
                  ({teamRows.length} staff)
                </span>
              )}
            </h2>

            <p>
              Scroll sideways if needed to see all days.
              {teamRows.length > 0 && (
                <span style={{ marginLeft: 10, fontWeight: 900 }}>
                  • {teamRows.length} staff scheduled
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="team-table-wrapper">
          <table className="team-schedule-table">
            <thead>
              <tr>
                <th className="col-staff sticky-col">Staff</th>
                <th className="col-role sticky-col-2">Role</th>
                {days.map((d, idx) => (
                  <th key={d} className={idx === todayCol ? "ts-today-th" : ""}>
                    {d}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {teamRows.map((member) => {
                const isMe = currentUserId != null && Number(member.user_id) === Number(currentUserId);

                return (
                  <tr key={member.user_id || member.name} className={isMe ? "team-row--me" : ""}>
                    <td className="col-staff sticky-col">
                      <span className="team-name-pill" title={member.name}>
                        <span className="team-dot" aria-hidden="true" />
                        {member.name}
                        {isMe ? <span className="team-me-tag">ME</span> : null}
                      </span>
                    </td>

                    <td className="col-role sticky-col-2" style={{ fontWeight: 850, color: "#64748b" }}>
                      {member.role}
                    </td>

                    {(member.scheduleText || []).map((valueObj, idx) => {
                      const entry = member.schedule?.[idx] || null;

                      const value =
                        typeof valueObj === "string"
                          ? valueObj
                          : valueObj?.label || "-";

                      const subValue =
                        typeof valueObj === "string"
                          ? ""
                          : valueObj?.subLabel || "";

                      return (
                        <td key={idx} style={{ background: "transparent" }}>
                          {idx === todayCol ? (
                            <div className="ts-today-cell">
                              <div style={getCellStyle(value, entry)} className="ts-cell">
                                {(() => {
                                  const upper = String(value || "").toUpperCase();
                                  const isAtt = ["NCNS", "SICK", "LATE", "EMERGENCY", "OTHER"].includes(upper);
                                  if (isAtt) return `${attendanceIcon(upper)} ${upper}`;

                                  const theme = getThemeForEntry(entry);
                                  const iconKey = theme?.icon;
                                  const iconEl = renderDeptIcon(iconKey, 14, theme?.color || "currentColor");

                                  if (iconEl) {
                                    return (
                                      <span
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: 8,
                                          justifyContent: "center",
                                        }}
                                      >
                                        <span style={{ color: theme?.color || "inherit" }}>{iconEl}</span>
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          <span>{value}</span>
                                          {subValue ? (
                                            <span
                                              style={{
                                                fontSize: "11px",
                                                fontWeight: 800,
                                                opacity: 0.9,
                                                marginTop: 2,
                                              }}
                                            >
                                              {subValue}
                                            </span>
                                          ) : null}
                                        </span>
                                      </span>
                                    );
                                  }

                                  return (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        lineHeight: 1.15,
                                      }}
                                    >
                                      <span>{value}</span>
                                      {subValue ? (
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            fontWeight: 800,
                                            opacity: 0.9,
                                            marginTop: 2,
                                          }}
                                        >
                                          {subValue}
                                        </span>
                                      ) : null}
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                          ) : (
                            <div style={getCellStyle(value, entry)} className="ts-cell">
                              {(() => {
                                const upper = String(value || "").toUpperCase();
                                const isAtt = ["NCNS", "SICK", "LATE", "EMERGENCY", "OTHER"].includes(upper);
                                if (isAtt) return `${attendanceIcon(upper)} ${upper}`;

                                const theme = getThemeForEntry(entry);
                                const iconKey = String(theme?.icon || "").trim();
                                const iconEl = renderDeptIcon(iconKey, 14, theme?.color || "currentColor");

                                if (iconEl) {
                                  return (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 6,
                                        justifyContent: "center",
                                      }}
                                    >
                                      <span style={{ color: theme?.color || "inherit" }}>
                                        {iconEl}
                                      </span>
                                      <span
                                        style={{
                                          display: "inline-flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          lineHeight: 1.15,
                                        }}
                                      >
                                        <span>{value}</span>
                                        {subValue ? (
                                          <span
                                            style={{
                                              fontSize: "11px",
                                              fontWeight: 800,
                                              opacity: 0.9,
                                              marginTop: 2,
                                            }}
                                          >
                                            {subValue}
                                          </span>
                                        ) : null}
                                      </span>
                                    </span>
                                  );
                                }

                                return (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      lineHeight: 1.15,
                                    }}
                                  >
                                    <span>{value}</span>
                                    {subValue ? (
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: 800,
                                          opacity: 0.9,
                                          marginTop: 2,
                                        }}
                                      >
                                        {subValue}
                                      </span>
                                    ) : null}
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="team-cards">
          {teamRows.map((member) => {
            const isMe = currentUserId != null && Number(member.user_id) === Number(currentUserId);
            return (
              <div key={member.user_id || member.name} className="member-card">
                <div className="member-head">
                  <div className="member-name" title={member.name}>
                    <span className="team-dot" aria-hidden="true" />
                    {member.name}
                    {isMe ? <span className="member-me">ME</span> : null}
                  </div>
                  <div className="member-role">{member.role}</div>
                </div>

                {days.map((d, idx) => {
                  const valueObj = member.scheduleText?.[idx] || "-";
                  const value =
                    typeof valueObj === "string"
                      ? valueObj
                      : valueObj?.label || "-";
                  const subValue =
                    typeof valueObj === "string"
                      ? ""
                      : valueObj?.subLabel || "";
                  const entry = member.schedule?.[idx] || null;
                  const chipStyle = getChipStyle(value, entry);

                  return (
                    <div key={d} className="day-row">
                      <div className="day-label">{d}</div>
                      <div className="day-chip">
                        <span
                          className="chip"
                          style={{
                            backgroundImage: chipStyle.backgroundImage,
                            backgroundSize: chipStyle.backgroundSize,
                            backgroundPosition: chipStyle.backgroundPosition,
                            color: chipStyle.color,
                            border: chipStyle.border,
                          }}
                        >
                          {(() => {
                            const upper = String(value || "").toUpperCase();
                            const isAtt = ["NCNS", "SICK", "LATE", "EMERGENCY", "OTHER"].includes(upper);
                            if (isAtt) return `${attendanceIcon(upper)} ${upper}`;

                            const theme = getThemeForEntry(entry);
                            const iconKey = theme?.icon;
                            const iconEl = renderDeptIcon(iconKey, 14, theme?.color || "currentColor");

                            if (iconEl) {
                              return (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    justifyContent: "center",
                                  }}
                                >
                                  {iconEl}
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      lineHeight: 1.15,
                                    }}
                                  >
                                    <span>{value}</span>
                                    {subValue ? (
                                      <span
                                        style={{
                                          fontSize: "10px",
                                          fontWeight: 900,
                                          opacity: 1,
                                          marginTop: 3,
                                          letterSpacing: "0.2px",
                                        }}
                                      >
                                        {subValue}
                                      </span>
                                    ) : null}
                                  </span>
                                </span>
                              );
                            }

                            return (
                              <span
                                style={{
                                  display: "inline-flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  lineHeight: 1.15,
                                }}
                              >
                                <span>{value}</span>
                                {subValue ? (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: 800,
                                      opacity: 0.9,
                                      marginTop: 2,
                                    }}
                                  >
                                    {subValue}
                                  </span>
                                ) : null}
                              </span>
                            );
                          })()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="team-legend">
          <LegendItem variant="off" text="Day off" textColor="#166534" />
          <LegendItem variant="vac" text="Vacation" textColor="#9a3412" />
          <LegendItem variant="train" text="Training" textColor="#854d0e" />
          <LegendItem variant="special" text="Special duty (Gatekeeper / Control Room / THAW)" textColor="#075985" />
        </div>
      </div>
    </div>
  );
}

function LegendItem({ variant, text, textColor }) {
  return (
    <div className="legend-item">
      <span className={`legend-dot legend-dot--${variant}`} />
      <span style={{ color: textColor || "#4b5563" }}>{text}</span>
    </div>
  );
}

export default TeamSchedulePage;